interface Closable {
    close: () => Promise<unknown>;
}

export interface OpenedConnection<TConnection extends Closable> {
    connection: TConnection;
    /** Settles once the connection is dead or due for replacement (expired token) */
    replaceWhen: Promise<void>;
}

interface ConnectionPoolOptions<TConnection extends Closable> {
    open: (key: string) => Promise<OpenedConnection<TConnection>>;
    retryDelayMs: number;
    /** How long an unused connection stays open for the next consumer */
    releaseGraceMs: number;
}

interface PoolEntry<TConnection extends Closable> {
    connection: TConnection | null;
    listeners: Set<() => void>;
    isDisposed: boolean;
    disposeTimer: ReturnType<typeof setTimeout> | null;
}

const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * One shared connection per key, reference counted by subscribers. A failed or
 * expired connection is replaced for everyone using it. Shaped for
 * `useSyncExternalStore`.
 */
export const createConnectionPool = <TConnection extends Closable>({
    open,
    retryDelayMs,
    releaseGraceMs,
}: ConnectionPoolOptions<TConnection>) => {
    const entries = new Map<string, PoolEntry<TConnection>>();

    const notify = (entry: PoolEntry<TConnection>) => {
        entry.listeners.forEach((listener) => listener());
    };

    const keepConnected = async (
        key: string,
        entry: PoolEntry<TConnection>,
    ) => {
        while (!entry.isDisposed) {
            try {
                const opened = await open(key);
                if (entry.isDisposed) {
                    await opened.connection.close();
                    return;
                }

                entry.connection = opened.connection;
                notify(entry);
                await opened.replaceWhen;
                entry.connection = null;
                notify(entry);
                await opened.connection.close();
            } catch {
                await sleep(retryDelayMs);
            }
        }
    };

    const dispose = (key: string, entry: PoolEntry<TConnection>) => {
        entry.isDisposed = true;
        entries.delete(key);
        void entry.connection?.close();
    };

    const subscribe = (key: string, listener: () => void) => {
        let entry = entries.get(key);
        if (!entry) {
            entry = {
                connection: null,
                listeners: new Set(),
                isDisposed: false,
                disposeTimer: null,
            };
            entries.set(key, entry);
            void keepConnected(key, entry);
        }

        const subscribedEntry = entry;
        if (subscribedEntry.disposeTimer) {
            clearTimeout(subscribedEntry.disposeTimer);
            subscribedEntry.disposeTimer = null;
        }
        subscribedEntry.listeners.add(listener);

        return () => {
            subscribedEntry.listeners.delete(listener);
            if (subscribedEntry.listeners.size > 0) {
                return;
            }
            subscribedEntry.disposeTimer = setTimeout(
                () => dispose(key, subscribedEntry),
                releaseGraceMs,
            );
        };
    };

    const getConnection = (key: string): TConnection | null =>
        entries.get(key)?.connection ?? null;

    return { subscribe, getConnection };
};
